/**
 * Service centralisé pour tous les calculs de pricing des garanties
 */

import type { Warranty, WarrantyPricing } from '../types/warranty.types';
import type { Society } from '@/features/societies/types/society.types';

/**
 * Calcule le pricing complet d'une garantie selon le type de société
 *
 * @param isTransferPrice option rétrocession de la configuration effective de la
 *   société (pro_v2_society_configuration) — jamais le champ legacy society.isTransferPrice
 */
export const calculateWarrantyPricing = (
  warranty: Warranty,
  society: Society,
  isIncluded = false,
  isTransferPrice = false,
): WarrantyPricing => {
  const sellingPriceTTC = warranty.sellingPrice;
  const sellingPriceHT = sellingPriceTTC / 1.2;
  const servicePriceHT = warranty.servicePrice;
  // Sans option rétrocession, aucun prix de transfert n'est pris en compte
  const headTransferPrice = isTransferPrice ? warranty.headTransferPrice || 0 : 0;
  const memberTransferPrice = isTransferPrice ? warranty.memberTransferPrice || 0 : 0;
  const provision = warranty.provision || 0;

  const isGroup = society.isGroup;
  const configGuarantorId = society.groupConfiguration?.guarantorId ?? society.ownConfiguration?.guarantorId ?? null;
  const hasExternalGuarantor = configGuarantorId && configGuarantorId !== society.uuid;

  let displayLabel: string;
  let displayValue: number;
  let calculatedRevenue: number;

  // CAS SPÉCIAL : INCLUSION
  if (isIncluded && headTransferPrice > 0 && memberTransferPrice === 0 && isGroup === 1) {
    displayLabel = `Montant facturé par ${society.parent?.name || 'le parent'}`;
    displayValue = sellingPriceHT - headTransferPrice;
    calculatedRevenue = displayValue;

    return {
      sellingPriceTTC,
      sellingPriceHT,
      servicePriceHT,
      headTransferPrice: 0,
      memberTransferPrice: displayValue,
      provision,
      commission: 0,
      calculatedRevenue,
      displayLabel,
      displayValue,
    };
  }

  // CAS 1 : SOCIÉTÉ INDÉPENDANTE (TYPE_COMPANY)
  if (isGroup === 0) {
    displayLabel = 'Prestation Affinicar';
    displayValue = servicePriceHT;
    calculatedRevenue = sellingPriceTTC;

    return {
      sellingPriceTTC,
      sellingPriceHT,
      servicePriceHT,
      headTransferPrice: null,
      memberTransferPrice: null,
      provision,
      commission: 0,
      calculatedRevenue,
      displayLabel,
      displayValue,
    };
  }

  // CAS 2 : AGENCE MEMBRE (TYPE_GROUP)
  if (isGroup === 1) {
    const parentName = society.parent?.name || 'le parent';

    if (!isTransferPrice) {
      displayLabel = 'Prestation Affinicar';
      displayValue = servicePriceHT;
      calculatedRevenue = sellingPriceTTC;
    } else if (headTransferPrice > 0 && memberTransferPrice === 0) {
      if (hasExternalGuarantor) {
        displayLabel = `Montant rétrocédé par ${parentName}`;
        displayValue = headTransferPrice;
        calculatedRevenue = sellingPriceTTC + headTransferPrice;
      } else {
        displayLabel = `Montant facturé par ${parentName}`;
        displayValue = headTransferPrice;
        calculatedRevenue = sellingPriceTTC - headTransferPrice;
      }
    } else if (headTransferPrice === 0 && memberTransferPrice > 0) {
      displayLabel = `Montant facturé par ${parentName}`;
      displayValue = memberTransferPrice;
      calculatedRevenue = sellingPriceTTC - memberTransferPrice;
    } else if (headTransferPrice > 0 && memberTransferPrice > 0) {
      displayLabel = `Montant facturé par ${parentName}`;
      displayValue = headTransferPrice;
      calculatedRevenue = sellingPriceTTC - headTransferPrice;
    } else {
      displayLabel = 'Prestation Affinicar';
      displayValue = servicePriceHT;
      calculatedRevenue = sellingPriceTTC;
    }

    return {
      sellingPriceTTC,
      sellingPriceHT,
      servicePriceHT,
      headTransferPrice: headTransferPrice || null,
      memberTransferPrice: memberTransferPrice || null,
      provision,
      commission: 0,
      calculatedRevenue,
      displayLabel,
      displayValue,
    };
  }

  // CAS 3 : TÊTE DE GROUPE (TYPE_HEAD)
  if (isGroup === 2) {
    if (!society.childrenCount || society.childrenCount === 0) {
      displayLabel = 'Prestation Affinicar';
      displayValue = servicePriceHT;
      calculatedRevenue = sellingPriceTTC;
    } else if (isTransferPrice && headTransferPrice > 0 && memberTransferPrice > 0) {
      displayLabel = 'Prestation Affinicar';
      displayValue = servicePriceHT;
      const marginPerMemberContract = memberTransferPrice - headTransferPrice;
      calculatedRevenue = sellingPriceTTC + marginPerMemberContract;
    } else {
      displayLabel = 'Prestation Affinicar';
      displayValue = servicePriceHT;
      calculatedRevenue = sellingPriceTTC;
    }

    return {
      sellingPriceTTC,
      sellingPriceHT,
      servicePriceHT,
      headTransferPrice: headTransferPrice || null,
      memberTransferPrice: memberTransferPrice || null,
      provision,
      commission: 0,
      calculatedRevenue,
      displayLabel,
      displayValue,
    };
  }

  // Cas par défaut
  return {
    sellingPriceTTC,
    sellingPriceHT,
    servicePriceHT,
    headTransferPrice: null,
    memberTransferPrice: null,
    provision,
    commission: 0,
    calculatedRevenue: sellingPriceTTC,
    displayLabel: 'Prestation Affinicar',
    displayValue: servicePriceHT,
  };
};

/**
 * Calcule le prix mensuel avec frais de service AJOUTÉS
 * Utilisé quand le CLIENT paie les frais
 */
export const calculateMonthlyPrice = (
  sellingPrice: number,
  monthlyPlateformFees: number,
): number => {
  if (monthlyPlateformFees <= 0) {
    return sellingPrice;
  }
  return sellingPrice / (1 - monthlyPlateformFees / 100);
};

export interface MonthlyBreakdown {
  /** Prix mensuel payé par le client (avec frais) */
  monthlyClient: number;
  /** Prix mensuel reçu par le vendeur (sans frais) */
  monthlySeller: number;
  /** Montant des frais mensuels */
  monthlyFee: number;
  /** Total sur la durée payé par le client */
  totalWithFees: number;
  /** Total sur la durée reçu par le vendeur */
  totalSeller: number;
  /** Pourcentage de frais */
  feePercentage: number;
}

/**
 * Calcule le détail complet d'une mensualisation avec frais de service.
 * Formule : totalAvecFrais = sellingPrice / (1 - fee/100), puis divisé par la durée.
 */
export const calculateMonthlyBreakdown = (
  sellingPrice: number,
  duration: number,
  feePercentage: number,
): MonthlyBreakdown => {
  if (!duration || duration <= 0) {
    return { monthlyClient: 0, monthlySeller: 0, monthlyFee: 0, totalWithFees: 0, totalSeller: sellingPrice, feePercentage };
  }
  const totalWithFees = calculateMonthlyPrice(sellingPrice, feePercentage);
  const monthlyClient = totalWithFees / duration;
  const monthlySeller = sellingPrice / duration;
  const monthlyFee = monthlyClient - monthlySeller;
  return {
    monthlyClient,
    monthlySeller,
    monthlyFee,
    totalWithFees,
    totalSeller: sellingPrice,
    feePercentage,
  };
};

/**
 * Extrait les frais INCLUS dans un prix mensuel
 * Utilisé quand le VENDEUR paie les frais
 */
export const extractMonthlyFees = (
  monthlyPriceWithFees: number,
  feePercentage: number,
): number => {
  if (feePercentage <= 0) {
    return 0;
  }
  return (monthlyPriceWithFees * feePercentage) / 100;
};

/**
 * Calcule le total sur toute la durée de mensualisation
 */
export const calculateTotalMonthlyPayment = (
  monthlyPrice: number,
  duration: number,
): number => {
  return monthlyPrice * duration;
};

/**
 * Calcule la commission/provision
 */
export const calculateProvision = (
  sellingPrice: number,
  provisionRate: number,
): number => {
  return (sellingPrice * provisionRate) / 100;
};

/**
 * Calcule le prix HT à partir du TTC
 */
export const calculatePriceHT = (priceTTC: number, vatRate = 20): number => {
  return priceTTC / (1 + vatRate / 100);
};

/**
 * Calcule le prix TTC à partir du HT
 */
export const calculatePriceTTC = (priceHT: number, vatRate = 20): number => {
  return priceHT * (1 + vatRate / 100);
};

/**
 * Vérifie si une garantie peut être mensualisée
 */
export const canBeMensualized = (warranty: Warranty, society: Society): boolean => {
  return (
    warranty.isMensualisable &&
    society.monthlyPayment === 2 &&
    warranty.sellingPrice > 0
  );
};

/**
 * Vérifie si le paiement en ligne est disponible
 */
export const isOnlinePaymentAvailable = (warranty: Warranty, society: Society): boolean => {
  const configGuarantorId = society.groupConfiguration?.guarantorId ?? society.ownConfiguration?.guarantorId ?? null;
  const hasExternalGuarantor = configGuarantorId && configGuarantorId !== society.uuid;
  const onlinePaymentEnabled = (typeof society.onlinePayment === 'number' && society.onlinePayment === 1) ||
                                (typeof society.onlinePayment === 'boolean' && society.onlinePayment === true);
  return (
    onlinePaymentEnabled &&
    hasExternalGuarantor === true &&
    warranty.sellingPrice > 0 &&
    !!society.stripeAccountId
  );
};

/**
 * Formate un montant en euros
 */
export const formatPrice = (amount: number, decimals = 2): string => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);
};

export const formatPriceHT = (amount: number): string => {
  return `${formatPrice(amount)} HT`;
};

export const formatPriceTTC = (amount: number): string => {
  return `${formatPrice(amount)} TTC`;
};
